import React, { Suspense, lazy, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { NavLink } from 'react-router-dom'
import { Drawer, Empty, Icon, message } from 'antd'
import ScannerErrorBoundary from '../../../shared/camera/ScannerErrorBoundary'
import ImagePreviewModal from '../../../shared/camera/ImagePreviewModal'
import { MAIN_MENU_ITEMS, OTHER_MENU_ITEMS, filterMenuItems } from '../../../shared/main-menu/mainMenuItems'
import { ROW_STATUS, ISSUE_STATUSES } from './handoverReducer'
import HandoverRow from './HandoverRow'
import BottomBar from './BottomBar'
import ConfirmModal from './ConfirmModal'
import ResultView from './ResultView'

const CameraCapture = lazy(() => import('../../../shared/camera/CameraCapture'))

const WARN_ROWS = 50

const filterRows = (rows, rowStatuses, filter, search) => {
    const keyword = search.trim().toUpperCase()

    return rows.filter((row) => {
        const status = rowStatuses[row.key]
        if (filter === 'issues' && !ISSUE_STATUSES.includes(status)) return false
        if (filter === 'ok' && status !== ROW_STATUS.OK) return false
        if (keyword && (row.name || '').indexOf(keyword) === -1) return false
        return true
    })
}

// Header gọn: nút menu (☰), tên màn, người đăng nhập
const Header = ({ username, onOpenMenu }) => (
    <div className="SewingHandoverHeader">
        <button type="button" className="SewingHandoverHeader-menu" onClick={onOpenMenu} aria-label="Mở menu">
            <Icon type="menu" />
        </button>
        <h1>Giao may bằng ảnh</h1>
        {username && <span className="SewingHandoverHeader-user">{username}</span>}
    </div>
)

Header.propTypes = {
    username: PropTypes.string,
    onOpenMenu: PropTypes.func.isRequired,
}

Header.defaultProps = {
    username: '',
}

// Dòng "Vừa đọc" trong badge đếm của camera: kết quả của ảnh vừa thêm (đọc QR + kiểm tra lô); tone đổi màu badge
const describeLastShot = (row, status, check) => {
    if (!row) return null
    if (status === ROW_STATUS.DECODING) return { text: 'Đang đọc QR...', tone: 'is-neutral' }
    // Không đọc được QR: chỉ báo ở dải vàng dưới khung chụp (cameraNotice), badge không thêm dòng
    if (status === ROW_STATUS.NO_QR) return null
    if (status === ROW_STATUS.NEED_PICK) return { text: `Ảnh có ${row.codes.length} mã · chọn lô sau khi chụp xong`, tone: 'is-warning' }
    if (status === ROW_STATUS.CHECKING) return { text: `Vừa đọc: ${row.name} · đang kiểm tra...`, tone: 'is-neutral' }
    if (status === ROW_STATUS.OK) return { text: `Vừa đọc: ${row.name} ✓`, tone: '' }
    if (status === ROW_STATUS.DUPLICATE) return { text: `Vừa đọc: ${row.name} · trùng lô đã chụp`, tone: 'is-error' }

    return { text: `Vừa đọc: ${row.name} · ${(check && check.message) || 'không giao được'}`, tone: 'is-error' }
}

const SewingHandoverPage = (props) => {
    const {
        state, rowStatuses, issueCount, issuePosition, okCount, totalItems, canSubmit, hint, partnerName, partnerInvalid,
        username, isAdmin, cameraOpen, confirming, previewKey, lastAddedKey,
        onOpenCamera, onCloseCamera, onCapture, onRemoveShot, onPickFiles, onAddManual, onRemoveRow, onPickCode, onSetName,
        onPreview, onPreviewChange, onJumpToIssue, onRecheck, onChangeFilter, onChangeSearch, onChangePartner,
        onPartnersLoaded, onOpenConfirm, onCancelConfirm, onSubmit, onReset, onViewSlip,
    } = props
    const { rows, checks, partner, filter, search, submitting, uploadPercent, banner, result } = state
    const libraryInputRef = useRef(null)
    const [menuOpen, setMenuOpen] = useState(false)

    const _menuAction = (fn) => () => {
        setMenuOpen(false)
        fn()
    }

    const menu = (
        <Drawer
            placement="left"
            visible={menuOpen}
            onClose={() => setMenuOpen(false)}
            title="Giao may bằng ảnh"
            width={260}
            className="SewingHandoverMenu"
        >
            <button type="button" disabled={submitting} onClick={_menuAction(() => libraryInputRef.current && libraryInputRef.current.click())}>
                <Icon type="picture" /> Chọn ảnh từ thư viện
            </button>
            <button type="button" disabled={submitting || !rows.length} onClick={_menuAction(onRecheck)}>
                <Icon type="reload" /> Kiểm tra lại các lô
            </button>
            <div className="SewingHandoverMenu-divider" />
            <div className="SewingHandoverMenu-title">Menu 3D</div>
            {[...filterMenuItems(MAIN_MENU_ITEMS, isAdmin), ...OTHER_MENU_ITEMS].map(item => (
                <NavLink key={item.to} exact={item.exact !== false} to={item.to} onClick={() => setMenuOpen(false)}>
                    {item.label}
                </NavLink>
            ))}
        </Drawer>
    )

    if (result) {
        return (
            <div className="SewingHandoverContainer is-result">
                <Header username={username} onOpenMenu={() => setMenuOpen(true)} />
                <div className="SewingHandoverBody">
                    <ResultView handover={result} onNew={onReset} onViewSlip={onViewSlip} />
                </div>
                {menu}
            </div>
        )
    }

    const visibleRows = filterRows(rows, rowStatuses, filter, search)
    // Dòng nhập tay không có ảnh: không có trong danh sách ảnh của camera và popup xem ảnh
    const previewRows = visibleRows.filter(row => row.previewUrl)
    const previewIndex = previewRows.findIndex(row => row.key === previewKey)
    const shots = rows.filter(row => row.file).map(row => ({ file: row.file, preview: row.previewUrl }))
    const lastRow = lastAddedKey ? rows.find(row => row.key === lastAddedKey) : null
    const lastShot = lastRow && describeLastShot(lastRow, rowStatuses[lastRow.key], lastRow.name ? checks[lastRow.name] : null)
    // Ảnh không đọc được QR: báo bằng dải vàng dưới khung chụp (mockup B), không dùng chip
    const cameraNotice = lastRow && rowStatuses[lastRow.key] === ROW_STATUS.NO_QR
        ? `⚠ Ảnh số ${lastRow.seq} không đọc được QR, nhập tên lô ở danh sách sau khi chụp`
        : null
    const cameraCountText = `Đã chụp ${shots.length} ảnh${issueCount ? ` · ${issueCount} cần xử lý` : ''}`
    const confirmBatches = rows
        .filter(row => rowStatuses[row.key] === ROW_STATUS.OK)
        .map(row => ({
            name: row.name,
            sku: (checks[row.name] || {}).sku,
            items: (checks[row.name] || {}).items_count || 0,
            hasImage: !!row.file,
        }))

    const tabs = [
        { key: 'all', label: 'Tất cả', count: rows.length },
        { key: 'issues', label: 'Cần xử lý', count: issueCount, danger: issueCount > 0 },
        { key: 'ok', label: 'Hợp lệ', count: okCount },
    ]

    return (
        <div className="SewingHandoverContainer">
            <Header username={username} onOpenMenu={() => setMenuOpen(true)} />

            <div className="SewingHandoverTabs" role="tablist">
                {tabs.map(tab => (
                    <button
                        key={tab.key}
                        type="button"
                        role="tab"
                        aria-selected={filter === tab.key}
                        className={`SewingHandoverTabs-item${filter === tab.key ? ' is-active' : ''}${tab.danger ? ' is-danger' : ''}`}
                        onClick={() => onChangeFilter(tab.key)}
                    >
                        {tab.label} <b>{tab.count}</b>
                    </button>
                ))}
            </div>

            <div className="SewingHandoverToolbar">
                <label className="SewingHandoverToolbar-search">
                    <Icon type="search" />
                    <input
                        type="search"
                        placeholder="Tìm lô trong danh sách"
                        value={search}
                        onChange={e => onChangeSearch(e.target.value)}
                    />
                </label>
                <button type="button" className="SewingHandoverToolbar-btn" disabled={submitting} onClick={onOpenCamera}>
                    <Icon type="camera" /> Chụp
                </button>
                <button
                    type="button"
                    className="SewingHandoverToolbar-btn"
                    disabled={submitting}
                    onClick={onAddManual}
                >
                    + Nhập tay
                </button>
                <input
                    ref={libraryInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    style={{ display: 'none' }}
                    onChange={(e) => {
                        onPickFiles(e.target.files)
                        e.target.value = ''
                    }}
                />
            </div>

            <div className="SewingHandoverBody">
                {rows.length > WARN_ROWS && (
                    <div className="SewingHandoverNotice is-warning">
                        Đang có {rows.length} ảnh (trên {WARN_ROWS}). Nên chia thành nhiều lượt giao.
                    </div>
                )}

                {banner && (
                    <div className="SewingHandoverNotice is-error" role="alert">
                        <b>{banner.title}</b> {banner.message}
                    </div>
                )}

                <div className="SewingHandoverList">
                    {rows.length === 0 && (
                        <Empty description="Bấm Chụp để chụp lệnh may, hoặc + Nhập tay khi QR không đọc được" />
                    )}
                    {rows.length > 0 && visibleRows.length === 0 && (
                        filter === 'issues' && !search.trim()
                            ? <div className="SewingHandoverList-done">✓ Không còn dòng cần xử lý</div>
                            : <Empty description="Không có dòng phù hợp" />
                    )}
                    {visibleRows.map(row => (
                        <HandoverRow
                            key={row.key}
                            row={row}
                            status={rowStatuses[row.key]}
                            check={row.name ? checks[row.name] : null}
                            duplicateOf={rowStatuses[row.key] === ROW_STATUS.DUPLICATE
                                ? (rows.find(r => r.name === row.name) || {}).seq
                                : null}
                            codeChecks={checks}
                            disabled={submitting}
                            onPreview={onPreview}
                            onPickCode={onPickCode}
                            onSetName={onSetName}
                            onRemove={onRemoveRow}
                        />
                    ))}
                </div>
            </div>

            <BottomBar
                partner={partner}
                partnerInvalid={partnerInvalid}
                totalBatches={okCount}
                totalItems={totalItems}
                issueCount={issueCount}
                issuePosition={issuePosition}
                canSubmit={canSubmit}
                submitting={submitting}
                hint={hint}
                onChangePartner={onChangePartner}
                onPartnersLoaded={onPartnersLoaded}
                onJumpToIssue={onJumpToIssue}
                onSubmit={onOpenConfirm}
            />

            <ConfirmModal
                visible={confirming}
                partnerName={partnerName}
                username={username}
                batches={confirmBatches}
                totalItems={totalItems}
                submitting={submitting}
                uploadPercent={uploadPercent}
                onOk={onSubmit}
                onCancel={onCancelConfirm}
            />

            {cameraOpen && (
                <ScannerErrorBoundary onClose={onCloseCamera}>
                    <Suspense fallback={null}>
                        <CameraCapture
                            title="Chụp lệnh may"
                            hint="Chụp rõ tờ lệnh may có mã QR · chụp liên tục, xong bấm Xong"
                            shots={shots}
                            countText={cameraCountText}
                            lastRead={lastShot}
                            cornerLabel={partnerName ? `Đối tác: ${partnerName}` : ''}
                            notice={cameraNotice}
                            onCapture={onCapture}
                            onPickFiles={onPickFiles}
                            onRemoveShot={onRemoveShot}
                            onClose={onCloseCamera}
                            onError={(msg) => {
                                onCloseCamera()
                                message.error(msg, 6)
                            }}
                        />
                    </Suspense>
                </ScannerErrorBoundary>
            )}

            <ImagePreviewModal
                images={previewRows.map(row => ({ url: row.previewUrl }))}
                index={previewIndex}
                onIndexChange={i => onPreviewChange(previewRows[i] ? previewRows[i].key : null)}
                onClose={() => onPreviewChange(null)}
            />

            {menu}
        </div>
    )
}

SewingHandoverPage.propTypes = {
    state: PropTypes.object.isRequired,
    rowStatuses: PropTypes.object.isRequired,
    issueCount: PropTypes.number.isRequired,
    issuePosition: PropTypes.number,
    okCount: PropTypes.number.isRequired,
    totalItems: PropTypes.number.isRequired,
    canSubmit: PropTypes.bool.isRequired,
    hint: PropTypes.string,
    partnerName: PropTypes.string,
    partnerInvalid: PropTypes.bool,
    username: PropTypes.string,
    isAdmin: PropTypes.bool,
    cameraOpen: PropTypes.bool,
    confirming: PropTypes.bool,
    previewKey: PropTypes.string,
    lastAddedKey: PropTypes.string,
    onOpenCamera: PropTypes.func.isRequired,
    onCloseCamera: PropTypes.func.isRequired,
    onCapture: PropTypes.func.isRequired,
    onRemoveShot: PropTypes.func.isRequired,
    onPickFiles: PropTypes.func.isRequired,
    onAddManual: PropTypes.func.isRequired,
    onRemoveRow: PropTypes.func.isRequired,
    onPickCode: PropTypes.func.isRequired,
    onSetName: PropTypes.func.isRequired,
    onPreview: PropTypes.func.isRequired,
    onPreviewChange: PropTypes.func.isRequired,
    onJumpToIssue: PropTypes.func.isRequired,
    onRecheck: PropTypes.func.isRequired,
    onChangeFilter: PropTypes.func.isRequired,
    onChangeSearch: PropTypes.func.isRequired,
    onChangePartner: PropTypes.func.isRequired,
    onPartnersLoaded: PropTypes.func.isRequired,
    onOpenConfirm: PropTypes.func.isRequired,
    onCancelConfirm: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onReset: PropTypes.func.isRequired,
    onViewSlip: PropTypes.func.isRequired,
}

SewingHandoverPage.defaultProps = {
    hint: '',
    issuePosition: 0,
    partnerName: '',
    partnerInvalid: false,
    username: '',
    isAdmin: false,
    cameraOpen: false,
    confirming: false,
    previewKey: null,
    lastAddedKey: null,
}

export default SewingHandoverPage
